/**
 * Support
 * Fundraising / donation section. Until a payment gateway exists, donations are
 * accepted by Interac e-Transfer and the phase meters show a graceful "coming
 * soon" state before any drive launches.
 */
import React, { useState } from 'react';
import { MASJID } from '../../config/masjid';
import './style.css';

// Fundraising phases for the masjid build.
// TODO: Drives have not launched yet, so every phase is at 0% and the section
// shows a graceful "launching soon" state automatically. When a drive opens,
// set the phase's `percent` (0–100) and the progress bar fills in.
const FUNDRAISING_PHASES = [
  { key: 'land', label: 'Land', percent: 0 },
  { key: 'construction', label: 'Construction', percent: 0 },
  { key: 'furnishing', label: 'Furnishing', percent: 0 },
];

const copyText = async (text) => {
  if (typeof navigator !== 'undefined' && navigator.clipboard?.writeText) {
    await navigator.clipboard.writeText(text);
    return;
  }

  if (typeof document === 'undefined') {
    throw new Error('Clipboard is unavailable');
  }

  const textarea = document.createElement('textarea');
  textarea.value = text;
  textarea.setAttribute('readonly', '');
  textarea.style.position = 'absolute';
  textarea.style.left = '-9999px';
  document.body.appendChild(textarea);
  textarea.select();
  document.execCommand('copy');
  document.body.removeChild(textarea);
};

const Support = (props) => {
  // TODO: Replace the Interac e-Transfer instructions with a real payment
  // gateway when one is selected for a future release.
  const donationFormUrl = MASJID.donationFormUrl;
  const interacEmail = MASJID.interacEmail;
  const [copyMessage, setCopyMessage] = useState('');

  // Before any drive launches every phase is 0% — show an encouraging
  // "coming soon" state instead of empty/zeroed progress meters.
  const driveLaunched = FUNDRAISING_PHASES.some((phase) => phase.percent > 0);

  const handleCopyInteracEmail = async () => {
    try {
      await copyText(interacEmail);
      setCopyMessage(`Interac email copied: ${interacEmail}`);
    } catch {
      setCopyMessage(`Copy failed. Please copy this email: ${interacEmail}`);
    }
  };

  return (
    <div className="support-area section-padding">
      <div className="container">
        <div className="row">
          <div className="col-lg-6">
            <div className="support-text">
              <span>Support the Project</span>
              <h2>Help Build the Masjid</h2>
              <p>
                Every contribution brings us closer to a permanent place of
                worship for Cole Harbour. Your sadaqah jariyah supports the
                land, construction, and furnishing of the masjid. Donations can
                currently be sent by Interac e-Transfer.
              </p>
              <div className="interac-donation">
                <span className="interac-donation-label">
                  Interac e-Transfer
                </span>
                <strong className="interac-email">
                  <span>imanhfx</span>
                  <span aria-hidden="true">&#8203;</span>
                  <span>@</span>
                  <span aria-hidden="true">&#8203;</span>
                  <span>gmail</span>
                  <span aria-hidden="true">&#8203;</span>
                  <span>.</span>
                  <span aria-hidden="true">&#8203;</span>
                  <span>com</span>
                </strong>
                <small>
                  Open your banking app, tap Interac e-Transfer / Send Money,
                  and use this email as the recipient.
                </small>
                <small>
                  Please include your name and "Masjid donation" in the transfer
                  message.
                </small>
              </div>
              <div className="btns">
                {donationFormUrl ? (
                  <a
                    href={donationFormUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="theme-btn-s3"
                  >
                    Register Interest
                  </a>
                ) : (
                  <button
                    type="button"
                    className="theme-btn-s3"
                    onClick={handleCopyInteracEmail}
                    title="Copy Interac e-Transfer email"
                  >
                    Donate via Interac e-Transfer
                  </button>
                )}
              </div>
              {copyMessage && (
                <p className="interac-copy-toast" role="status">
                  {copyMessage}
                </p>
              )}
            </div>
          </div>
          <div className="col-lg-6">
            <div className="fundraising">
              <div className="fundraising-head">
                <h3>Our Fundraising Journey</h3>
                {!driveLaunched && (
                  <span className="fundraising-badge">
                    Drives launching soon, insha&apos;Allah
                  </span>
                )}
              </div>

              <ul className="fundraising-list">
                {FUNDRAISING_PHASES.map((phase, index) => {
                  const pending = phase.percent <= 0;
                  return (
                    <li
                      key={phase.key}
                      className={`fundraising-item${pending ? ' is-pending' : ''}`}
                    >
                      <div className="fundraising-row">
                        <span className="fundraising-label">
                          <span className="fundraising-step">{index + 1}</span>
                          {phase.label}
                        </span>
                        <span className="fundraising-value">
                          {pending ? 'Coming soon' : `${phase.percent}%`}
                        </span>
                      </div>
                      <div className="fundraising-track">
                        <span
                          className="fundraising-fill"
                          style={{
                            width: `${Math.max(0, Math.min(100, phase.percent))}%`,
                          }}
                        ></span>
                      </div>
                    </li>
                  );
                })}
              </ul>

              {!driveLaunched && (
                <p className="fundraising-note">
                  The journey is just beginning — be among the first to
                  contribute. Register your interest and we&apos;ll let you know
                  the moment our drives open.
                </p>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Support;
